/**
 * Constants for planner routing source
 */ export var PlannerRouteChangeSource = /*#__PURE__*/ function(PlannerRouteChangeSource) {
    PlannerRouteChangeSource[PlannerRouteChangeSource["MyTasks"] = 0] = "MyTasks";
    PlannerRouteChangeSource[PlannerRouteChangeSource["Other"] = 1] = "Other";
    PlannerRouteChangeSource[PlannerRouteChangeSource["LeftNav"] = 2] = "LeftNav";
    return PlannerRouteChangeSource;
}({});
// Constants
import { projectManagerAgentId } from "@ms/uno-constants/lib/local/AgentConstants";
import { PlanType } from "@ms/uno-constants/lib/local/AppConstants";
import { EngagementTelemetryConstants } from "@ms/uno-constants/lib/local/EngagementTelemetryConstants";
import { NotificationChannel, NotificationType } from "@ms/uno-constants/lib/local/NotificationConstants";
import { SubRouteType } from "@ms/uno-constants/lib/local/RoutingConstants";
import { StoreEntityState, StoreEntityStatus, failedStoreEntityStatuses } from "@ms/uno-constants/lib/local/StoreConstants";
import { MinCharsNeededToSearch } from "@ms/uno-constants/lib/local/UIBehaviorConstants";
import { ViewElementIds } from "@ms/uno-constants/lib/local/UIConstants";
import { ViewRenderState } from "@ms/uno-constants/lib/local/ViewConstants";
import { MyTasksViewPivotType } from "@ms/uno-constants/lib/local/ViewPivots";
import { CopilotWorkflowUnifiedState } from "@ms/uno-constants/lib/local/WorkflowConstants";
// Controls
import { Spinner, SpinnerSize, mergeStyles } from "@fluentui/react";
import { DataFetchingComponent } from "../../DataFetchingComponent";
import { AddIconPlacement, LayoutStyle, TaskAssignmentControl } from "../../presentational/assignmentControl/TaskAssignmentControl";
import { ChatThreadView } from "../../presentational/chatThreadView/ChatThreadView";
import { CheckUnsavedApprovalDialog } from "../../presentational/checkUnsavedApprovalDialog/CheckUnsavedApprovalDialog";
import { CommentThreadView, NewCommentDisplayMode } from "../../presentational/commentThreadView/CommentThreadView";
import { CustomRecurrenceDialog } from "../../presentational/customRecurrenceDialog/CustomRecurrenceDialog";
import { DeleteRecurringTaskDialog } from "../../presentational/deleteRecurringTaskDialog/DeleteRecurringTaskDialog";
import { LazyFormResponseViewDialog } from "../../presentational/formResponseViewDialog/LazyFormResponseViewDialog";
import { OneDrivePickerForAttachments, getOneDrivePickerStrings } from "../../presentational/oneDrivePickerForAttachments/OneDrivePickerForAttachments";
import { TaskEditorDialog, TaskEditorDialogFocusOptions } from "../../presentational/taskEditor/dialog/TaskEditorDialog";
import { LazyToastContainer } from "../LazyToastContainer";
// Models
import { EntityActionEventType, FeedbackResponseValue, TraceSignalType } from "@augloop-types/planner-file-recommendations";
import { PlannerChatThreadType } from "@ms/planner-models/lib/local/service/planner/chat/IChatMessageResource";
import { getProjectManagerAgent } from "@ms/uno-models/lib/local/client/agent/ProjectManagerAgent";
import { ApprovalUserRole } from "@ms/uno-models/lib/local/client/legacyPlanner/approval/ApprovalUserRole";
import { Attachment } from "@ms/uno-models/lib/local/client/legacyPlanner/attachment/Attachment";
import { PlanDetails } from "@ms/uno-models/lib/local/client/legacyPlanner/plan/PlanDetails";
import { PlanEntitySet } from "@ms/uno-models/lib/local/client/legacyPlanner/plan/PlanEntitySet";
import { PreviewType } from "@ms/uno-models/lib/local/client/legacyPlanner/previewType/PreviewType";
import { Assignment } from "@ms/uno-models/lib/local/client/legacyPlanner/task/Assignment";
import { TaskKind } from "@ms/uno-models/lib/local/client/legacyPlanner/task/ITask";
import { Task } from "@ms/uno-models/lib/local/client/legacyPlanner/task/Task";
import { TaskDetails } from "@ms/uno-models/lib/local/client/legacyPlanner/task/TaskDetails";
import { TaskEntitySet } from "@ms/uno-models/lib/local/client/legacyPlanner/task/TaskEntitySet";
import { TaskValidationConstants } from "@ms/uno-models/lib/local/client/legacyPlanner/task/TaskValidationConstants";
import { ConvertPercentCompleteToProgressState, ConvertPriorityNameToPriority, ConvertPriorityToPriorityName, ConvertProgressStateToPercentComplete } from "@ms/uno-models/lib/local/client/legacyPlanner/utilities/ConversionUtilities";
import { ApprovalPermissions } from "@ms/uno-models/lib/local/client/permissions/ApprovalPermissions";
import { AttachmentPermissions, isAttachmentPermissionDisabled } from "@ms/uno-models/lib/local/client/permissions/AttachmentPermissions";
import { FormPermissions } from "@ms/uno-models/lib/local/client/permissions/FormPermissions";
import { DefaultTaskPermissions } from "@ms/uno-models/lib/local/client/permissions/ITaskPermissions";
import { MandatoryChecklistTogglePermissions } from "@ms/uno-models/lib/local/client/permissions/MandatoryChecklistTogglePermissions";
import { GroupWorkLoad } from "@ms/uno-models/lib/local/client/unifiedGroup/GroupWorkLoad";
import { FormerMember } from "@ms/uno-models/lib/local/client/user/FormerUser";
import { UnknownUser } from "@ms/uno-models/lib/local/client/user/UnknownUser";
import { ContainerType } from "@ms/uno-models/lib/local/service/graph-legacy/container/ContainerType";
import { ContentType } from "@ms/uno-models/lib/local/service/graph/general/ContentType";
import { getTaskCommentEmailSubject } from "@ms/uno-models/lib/local/utilities/CommentUtilities";
import { generateEntityLink } from "@ms/uno-models/lib/local/utilities/EntityLinkUtilities";
import { generateFakeCreationPost } from "@ms/uno-models/lib/local/utilities/TaskActivityFeedUtilities";
// Styles
import { plannerLogoOutlineIconProps } from "../../../icons/icons.styles";
import { getPlannerAppDialogContentStyles, getPlannerAppDialogStyles } from "../../../utilities/BaseDialog.styles";
import { appSpinnerStyles } from "../../../utilities/Mixin.styles";
import { getLoadingSpinnerStyles } from "./TaskEditorContainer.styles";
// telemetry
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
// Utilities
import { ThemeContext } from "@fluentui/react";
import { LabelConstants, getLabelDataByCopilotWorkflowStatus } from "@ms/uno-models/lib/local/utilities/LabelUtilities";
import { Strings } from "@ms/uno-resources/lib/local/CultureUtilities";
import { extractMyTasksRoutingDetailsFromSubRoute, extractPremiumPlanRoutingDetailsFromExternalTaskDetails } from "@ms/uno-routing";
import { isStatusFailure, isStatusFetchingOrUnfetched } from "@ms/uno-stores/lib/local/utilities/StatusUtilities";
import { SparseNumberArray } from "@ms/uno-utilities/lib/local/collections/SparseNumberArray";
import { sanitizeContent } from "@ms/uno-utilities/lib/local/ContentSanitizationUtilities";
import { generateOrderHintBetween } from "@ms/uno-utilities/lib/local/OrdinalUtilities";
import { getRedirectRootUrl } from "@ms/uno-utilities/lib/local/UrlUtilities";
import map from "lodash/map";
import React, { Suspense } from "react";
/**
 * task editor wrapper which contains
 *  - A Dialog which contains the TaskDetailsEditor
 *  - Customizations of the FabricDialog chrome
 */ export class TaskEditorContainer extends DataFetchingComponent {
    name() {
        return "TaskEditorContainer";
    }
    logUsage(action, extraData, exportName) {
        this.props.loggers.engagementLogger.logEngagement(this.name(), action, extraData, exportName);
    }
    fetchCriticalDataWhenComponentDidUpdate(prevProps, prevState) {
        // This can happen if we navigate to a deep-link while task editor is open for another task
        return this.props.taskId !== prevProps.taskId;
    }
    async fetchCriticalData(props, state) {
        const { actionCreatorProviders, taskId } = this.props;
        // Resolve the task entity set
        const taskActionCreator = await actionCreatorProviders.basicTask();
        taskActionCreator.fetchTaskEntityGroup(taskId);
        const { planStore, task } = this.state;
        if (planStore == null || task == null) {
            return;
        }
        const plan = planStore.getPlan(task.planId).data;
        const planDetails = planStore.getPlanDetails(task.planId);
        if (planDetails == null) {
            const planActionCreator = await actionCreatorProviders.basicPlan();
            planActionCreator.fetchPlan(task.planId, PlanType.Basic);
        }
        // TODO: PBI #8705025 - Move this to view AC and fetch other container types as well
        if (plan != null) {
            if (plan.container.containerType === ContainerType.Group) {
                const [containerActionCreator, groupConversationCreator] = await Promise.all([
                    actionCreatorProviders.container(),
                    actionCreatorProviders.groups()
                ]);
                const { containerType, externalId } = plan.container;
                containerActionCreator.fetchContainer({
                    containerType,
                    externalId
                });
                if (task.conversationThreadId != null && plan?.container.externalId) {
                    groupConversationCreator.fetchConversationThread(plan.container.externalId, task.conversationThreadId);
                }
            }
        }
        //do not fetch buckets for todo tasks because they do not have buckets
        if (task.kind !== TaskKind.Todo) {
            const bucketActionCreator = await actionCreatorProviders.basicBucket();
            bucketActionCreator.fetchBucketsForPlan(task.planId);
        }
        if (task.creationSource?.publication != null || task.isProjectTask()) {
        // TODO: PBI #8683830 - Implement fetchPermissionsWithTaskContext
        // const containerActionCreator = await actionCreatorProviders.container();
        // containerActionCreator.fetchPermissionsWithTaskContext(task);
        }
    }
    fetchNonCriticalData(props, state) {
        this.fetchUsersAndSharedContainers();
        // fetch approval detail information
        if (this.areApprovalDetailsNeeded()) {
            this.fetchApprovalDetailsForTask();
        }
        this.fetchChatMessages();
    }
    // Override the data fetching component function to enable the fetching of non-critical data
    fetchNonCriticalDataWhenComponentDidUpdate(prevProps, prevState) {
        // Since we are using taskStore to sync the task details, we cannot use prevState or prevProps to detect whether approval data is changed
        // So for here we just check if the approval details is empty, if so we fetch it.
        // To update the approval details, we use the callback in TaskEditor and use TaskEditor.componentDidUpdate to detect changes
        const { task, taskStore } = this.state;
        if (task == null || taskStore == null) {
            return false;
        }
        const taskDetails = taskStore.getTaskDetails(task.id);
        const taskApprovalDetails = taskStore.getTaskApprovalDetails(task.id);
        // If task has approval, but approval details are not fetched, fetch them
        if (this.areApprovalDetailsNeeded() && taskDetails?.approval && taskApprovalDetails?.data == null && taskApprovalDetails?.status === StoreEntityStatus.Unfetched) {
            return true;
        }
        const { planStore, userStore, driveItemStore } = this.state;
        // check for user and shared container data
        if (planStore == null || userStore == null || driveItemStore == null) {
            return false;
        }
        const plan = planStore.getPlan(task.planId).data;
        if (plan == null || plan.container == null) {
            return false;
        }
        // if PrevPlan or prevPlan.container is null, or plan.id or plan.container.externalId is changed, re-fetch the data
        const shouldFetchNonCriticalData = this.prevPlan == null || this.prevPlan.container == null || plan.id !== this.prevPlan.id || plan.container.externalId !== this.prevPlan.container.externalId;
        this.prevPlan = plan;
        return shouldFetchNonCriticalData;
    }
    async componentDidMount() {
        const { storeProviders, taskId, onOpen, configProvider } = this.props;
        const [appContextStore, bucketStore, userMembershipStore, driveItemStore, intelligenceStore, permissionsStore, planStore, taskStore, containerStore, userStore, conversationStore, chatStore, modalStore, tenantStore, goalStore] = await Promise.all([
            storeProviders.appContext(),
            storeProviders.basicBucket(),
            storeProviders.userMembership(),
            storeProviders.driveItem(),
            storeProviders.intelligence(),
            storeProviders.basicPermissions(),
            storeProviders.basicPlan(),
            storeProviders.basicTask(),
            storeProviders.legacyContainer(),
            storeProviders.user(),
            storeProviders.conversations(),
            storeProviders.chat(),
            storeProviders.modal(),
            storeProviders.tenant(),
            storeProviders.basicGoal()
        ]);
        // We can assume task is in store
        const task = taskStore.getTask(taskId).data;
        this.setState({
            task,
            appContextStore,
            bucketStore,
            userMembershipStore,
            driveItemStore,
            intelligenceStore,
            permissionsStore,
            planStore,
            taskStore,
            containerStore,
            userStore,
            conversationStore,
            chatStore,
            modalStore,
            tenantStore,
            goalStore
        });
        taskStore.subscribeToStore(this.onTaskStoreUpdate);
        this.addStoresToSubscribeTo([
            appContextStore,
            bucketStore,
            userMembershipStore,
            driveItemStore,
            intelligenceStore,
            permissionsStore,
            planStore,
            taskStore,
            containerStore,
            userStore,
            conversationStore,
            chatStore,
            modalStore,
            tenantStore,
            goalStore
        ]);
        // set agents
        this.setAgents();
        super.componentDidMount();
        this.fetchFileSuggestions(task, taskStore.getTaskDetails(task.id));
        const { flights } = configProvider();
        if (flights.EnableFileIntelligence) {
            // In order to capture the base of base/seen/tried/kept, we'll log the task editor open as well as the current number of attachments
            this.logUsage("FileRecsOpenTaskEditor", {
                taskId: task.id,
                hasAttachments: String(task.numberOfReferences > 0)
            });
        }
        onOpen();
        this.logUsage("TaskEditor Opened", {
            taskId: task.id,
            planId: task.planId,
            taskCreationSourceType: task.taskCreationSourceType
        });
    }
    componentWillUnmount() {
        super.componentWillUnmount();
        // Unsubscribe from task store callback
        this.state.taskStore?.unsubscribeFromStore(this.onTaskStoreUpdate);
    }
    componentDidCatch(error, errorInfo) {}
    renderComponent() {
        const { dialogClassName, taskAccessContext, actionCreatorProviders, configProvider, taskId } = this.props;
        const { task, currentTaskEditorFocus, appContextStore, bucketStore, driveItemStore, intelligenceStore, planStore, taskStore, containerStore, permissionsStore, userStore, tenantStore, goalStore } = this.state;
        const storeStatus = taskStore?.getTask(taskId).status;
        if (storeStatus != null && isStatusFailure(storeStatus)) {
            this.closeEditor();
            return null;
        }
        if (task == null || permissionsStore == null || taskStore == null || planStore == null || userStore == null || bucketStore == null || containerStore == null || driveItemStore == null || appContextStore == null || intelligenceStore == null) {
            return /*#__PURE__*/ React.createElement(ThemeContext.Consumer, null, (theme)=>this.renderLoadingSpinner(theme));
        }
        const appContext = appContextStore.getAppContext();
        const currentUserId = appContext.userId;
        const currentUser = userStore.getUserById(currentUserId || "").data;
        const taskDetails = taskStore.getTaskDetails(task.id);
        const taskApprovalDetails = taskStore.getTaskApprovalDetails(task.id);
        const plan = planStore.getPlan(task.planId).data;
        const containerExternalId = plan ? plan.container.externalId : null;
        const storeContainer = containerExternalId != null ? containerStore.getContainer(containerExternalId).data : null;
        const storeGroupContainer = storeContainer?.containerType === ContainerType.Group ? storeContainer : null;
        const groupDetails = storeGroupContainer?.unifiedGroupDetails ?? null;
        const tenantFeedbackSettingsData = tenantStore?.getCurrentTenantFeedbackSettings().data || null;
        const isAITask = task.internalProperties.copilot?.isAiTask;
        const taskCreationDate = task?.createdDate;
        const { clientBasicLimits } = configProvider().settings;
        const { maxAttachmentsOnTask, maxRequiredFormsOnTask, maxRequiredApprovalsOnTask, maxTaskAssignees, maxFileSuggestions } = clientBasicLimits;
        const { changeGates, flights } = configProvider();
        if (!currentUser) {
            return null;
        }
        const taskLimits = {
            maximumAssignees: maxTaskAssignees,
            maximumAttachments: maxAttachmentsOnTask,
            maxCategoryNameLength: PlanDetails.MaxCategoryNameLength,
            maxDescriptionLength: TaskDetails.DescriptionMaxLength,
            maxChecklistItemsPerTask: TaskDetails.MaxChecklistItemsPerTask,
            maxTitleLength: TaskValidationConstants.TitleMaxLength,
            maxFileSuggestions,
            maxRequiredFormsOnTask: maxRequiredFormsOnTask,
            maxRequiredApprovalsOnTask: maxRequiredApprovalsOnTask
        };
        const taskEditorCallbacks = {
            convertPercentCompleteToProgressState: ConvertPercentCompleteToProgressState,
            convertProgressStateToPercentComplete: ConvertProgressStateToPercentComplete,
            convertPercentCompleteToProgressText: Task.getProgressTitle,
            convertPriorityToPriorityName: ConvertPriorityToPriorityName,
            convertPriorityNameToPriority: ConvertPriorityNameToPriority,
            convertPriorityToPriorityText: Task.getPriorityText,
            getAttachmentPreviewThumbnail: (attachmentUrl)=>{
                if (!groupDetails) {
                    return null;
                }
                return driveItemStore.getDriveItemThumbnail(groupDetails.id, attachmentUrl).data;
            },
            getDocumentUploadSession: (sessionId)=>driveItemStore.getUploadSession(sessionId).data ?? null,
            getUserById: (id)=>userStore.getUserById(id).data,
            onOpenErrorDialog: this.onOpenErrorDialog,
            onAlertUserDescriptionUpdated: async ()=>{
                const notificationActionCreator = await actionCreatorProviders.notification();
                notificationActionCreator.enqueueHiddenToast("SharedComponents_TaskDescriptionSavedToastTitle");
            },
            onUpdatePlanDetails: this.onUpdatePlanDetails,
            onCreateTask: this.onCreateTask,
            onUpdateTask: this.onUpdateTask,
            onUpdateTaskDetails: this.onUpdateTaskDetails,
            onUploadFileAsAttachment: async (file, taskDetails, containerId, previewPriority)=>{
                this.logUsage(EngagementTelemetryConstants.AddAttachment, {
                    type: "File",
                    entityId: task.id
                });
                const taskActionCreator = await actionCreatorProviders.basicTask();
                taskActionCreator.uploadAttachmentForTask(file, taskDetails, containerId, previewPriority);
            },
            onSelectEmptyFile: async (fileName)=>{
                const notificationActionCreator = await actionCreatorProviders.notification();
                notificationActionCreator.enqueueToast("Toasts_CantAttachEmptyFile", [
                    fileName
                ]);
            },
            onCopyLinkToTask: this.onCopyLinkToTask,
            onOpenOnedrivePicker: this.onOpenOnedrivePicker.bind(this, containerExternalId),
            onOpenCopyTaskDialog: this.openCopyTaskDialog,
            openMoveTaskDialog: this.openMoveTaskDialog,
            openDeleteRecurringTaskDialog: this.openDeleteRecurringTaskDialog,
            onDeleteTask: this.onDeleteTask,
            onOpenAttachmentEditorDialog: this.openAttachmentEditorDialog,
            onOpenCustomRecurrenceDialog: this.onOpenCustomRecurrenceDialog,
            onNavigateToPlan: this.onNavigateToPlan,
            onOpenLink: this.openAttachmentInHost,
            onAddSuggestionAsAttachment: this.convertSuggestionToAttachment,
            onFileSuggestionsSeenStateChange: this.onFileSuggestionsSeen,
            renderAssignmentControl: this.renderAssignmentControl,
            renderCommentThreadViewControl: this.renderCommentThreadViewControl,
            renderDeleteRecurringTaskDialog: this.renderDeleteRecurringTaskDialog.bind(this, plan),
            renderOnedrivePicker: this.renderOnedrivePicker.bind(this, taskDetails, groupDetails),
            renderCustomRecurrenceDialog: this.renderCustomRecurrenceDialog,
            copyLinkToSuggestion: this.copyLinkToSuggestion,
            onOpenFileSuggestionContextMenu: this.onOpenFileSuggestionContextMenu,
            fetchFileSuggestions: this.fetchFileSuggestions,
            onOpenAddFormRequirementDialog: this.onOpenAddFormRequirementDialog,
            // TODO: PBI #8680215 - Add Form Dialog when ported.
            renderAddFormRequirementDialog: ()=>null,
            onOpenRemoveFormConfirmationDialog: this.onOpenRemoveFormConfirmationDialog,
            // TODO: PBI #8680216 - Add Remove Form Confirmation Dialog when ported.
            renderRemoveFormConfirmationDialog: ()=>null,
            onCopyAttachmentLink: this.onCopyAttachmentLink,
            onOpenFormResponseViewDialog: this.onOpenFormResponseViewDialog,
            // TODO: PBI #8680217 - Add Form Response Dialog when ported.
            renderFormResponseViewDialog: this.renderFormResponseViewDialog,
            onOpenEditFormDialog: this.onOpenEditFormDialog,
            // TODO: PBI #8680218 - Add Edit Form Dialog when ported.
            renderEditFormDialog: ()=>null,
            onOpenIframeFormRequirementDialog: this.onOpenIframeFormRequirementDialog,
            getAssigneePoolForApproval: this.getAssigneePoolForPlan,
            getUserPhoto: this.getUserPhoto,
            onCancelApproval: this.onCancelApproval,
            onClearApproval: this.onClearApproval,
            onUpdateApproval: this.onUpdateApproval,
            onSendApprovalRequest: this.onSendApprovalRequest,
            onSelectApprover: this.onSelectApprover,
            onReloadApprovalData: this.fetchApprovalDetailsForTask,
            renderCheckUnsavedApprovalDialog: this.renderCheckUnsavedApprovalDialog,
            onClickGoToPremiumPlan: this.onClickGoToPremiumPlan,
            onClickStartAppPoweredTask: this.openAppPoweredLinkInHost,
            getCopilotWorkflowStatusLabel: this.getCopilotWorkflowStatusLabel,
            onOpenChatPanel: this.onOpenChatPanel,
            renderChatThreadControl: this.renderChatThreadViewControl,
            getGoalById: (goalId)=>goalStore?.getGoal(goalId).data ?? null
        };
        const taskPermissions = permissionsStore.getTaskPermissions(task, plan) ?? DefaultTaskPermissions;
        const containerPermissions = storeContainer ? permissionsStore.getContainerPermissions(storeContainer) : permissionsStore.getDefaultContainerPermissions(plan?.container);
        const enableFileSuggestions = flights.EnableFileIntelligence && !isAttachmentPermissionDisabled(taskPermissions.attachmentPermissions, AttachmentPermissions.DisableUpdateAttachments);
        const enablePlanTitleNavLink = this.shouldEnablePlanTitleNavLink();
        const taskEditorConfig = {
            enableComments: taskPermissions.canComment,
            enableFileUpload: taskPermissions.canUploadFiles,
            enableLastModifiedBy: true,
            limits: taskLimits,
            enableCopyTask: taskPermissions.canCopy,
            enableCopyLinkToTask: true,
            enableMoveTask: taskPermissions.canMove,
            permissions: taskPermissions,
            containerPermissions: containerPermissions,
            enableOpenInNewWindow: false,
            enableFileSuggestions,
            enableRecurringTasks: true,
            enableRichTextEditor: true,
            mandatoryChecklistTogglePermissions: MandatoryChecklistTogglePermissions.HideMandatoryToggle,
            enablePlanTitleNavLink,
            enableProjectTasksForPlannerWeb: true,
            enableMandatoryCheckList: true,
            enableExtraFieldsForPersonalTasks: true,
            enableRibbon: false,
            disableNotesCollapsingBehavior: true,
            enableAddRequirements: false,
            enableFormsRequirement: flights.EnableMandatoryForms && task.kind !== TaskKind.Todo,
            enableApprovalsRequirement: flights.EnableMandatoryApprovals && task.kind !== TaskKind.Todo,
            enableMissingFieldsMessageForPersonalTasks: false,
            formPermissions: FormPermissions.DisableUpdateFormRequirement | FormPermissions.DisableViewAllFormResponses,
            approvalPermissions: ApprovalPermissions.DisableUpdateApprovalRequirement,
            enableAPTSection: flights.EnableAppPoweredTasks,
            enableBasicPlanTaskChatMentions: this.shouldEnableBasicPlanTaskChatMentions(),
            disableExtraHostnameUrlValidation: changeGates.DisableExtraHostnameUrlValidation,
            enableGoalsNameInTaskEditor: flights.EnableGoalsNameInTaskEditor,
            enablePlanInTeamsChannel: flights.EnablePlanInTeamsChannel,
            disableCopyTaskBySensitivity: flights.EnableContentLevelSensitivityLabels && permissionsStore.getTaskPermissions(task, plan)?.canCopy === false && permissionsStore.getPlanPermissionsFromUserRightsObject(task.planId)?.canCopyAllowedBySensitivity === false,
            disableTaskNotesEscapeCharacterValidation: changeGates.DisableTaskNotesEscapeCharacterValidation,
            isRoosterV9EditorEnabled: flights.EnableRoosterV9ForRTE
        };
        return /*#__PURE__*/ React.createElement(ThemeContext.Consumer, null, (theme)=>{
            const taskDetailsEditorProps = {
                config: taskEditorConfig,
                callbacks: taskEditorCallbacks,
                container: storeContainer,
                currentUser: currentUser,
                plan: plan,
                planDetails: planStore.getPlanDetails(task.planId),
                // TODO: PBI #8680219 Fix when Aggregate Store is available.
                planLabelCounts: new SparseNumberArray(),
                planBuckets: plan ? bucketStore.getBucketsForPlan(plan.id).data : [],
                bucket: task.bucketId != null ? bucketStore.getBucket(task.bucketId).data : null,
                task: task,
                taskDetails: taskDetails,
                taskApprovalDetails: taskApprovalDetails,
                attachmentToEdit: this.state.attachmentToEdit,
                fileSuggestions: this.getDedupedFileSuggestions(task),
                fileSuggestionsContainerRef: this.fileSuggestionsContainerRef,
                scrollContainerRef: this.scrollContainerRef,
                taskAccessContext,
                theme,
                createdFormId: this.state.createdFormId,
                selectedApprover: this.state.selectedApprover,
                isApprovalDataLoading: taskStore.isTaskApprovalOperationInProgress(task.id),
                userRole: this.getUserRole(),
                uploadPendingAttachments: taskStore.getInProgressUploadsForTask(task.id),
                children: this.props.children,
                tenantFeedbackSettings: tenantFeedbackSettingsData,
                appContext: appContext,
                isAITask: isAITask,
                taskCreationDate: taskCreationDate
            };
            const dialogStyles = getPlannerAppDialogStyles(theme);
            const dialogContentStyles = getPlannerAppDialogContentStyles(theme);
            return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(TaskEditorDialog, {
                focusOverride: currentTaskEditorFocus,
                dialogClassName: dialogClassName,
                styles: dialogStyles,
                dialogContentStyles: dialogContentStyles,
                editorProps: taskDetailsEditorProps,
                fileSuggestionsSeen: intelligenceStore.getFileSuggestionsSeenState(task.id) ?? null,
                onDismiss: this.onDismissTaskEditorDialog,
                theme: theme,
                layerProps: {
                    ...this.props.layerProps,
                    fabricProps: {
                        id: ViewElementIds.BasicTaskDetailsView
                    }
                },
                focusTrapZoneProps: this.props.focusTrapZoneProps
            }), this.renderToastContainer());
        });
    }
    /**
     * Sets the agents for the task editor
     */ async setAgents() {
        const { agentProviders } = this.props;
        if (agentProviders) {
            const projectManagerAgentGetter = await agentProviders.projectManager();
            this.setState({
                agents: [
                    projectManagerAgentGetter()
                ]
            });
        }
    }
    checkForCriticalDataReady() {
        const { taskStore } = this.state;
        if (taskStore == null) {
            return false;
        }
        return taskStore.getTaskDetails(this.props.taskId) != null;
    }
    getRenderState() {
        const { taskStore, bucketStore, conversationStore, planStore, driveItemStore, containerStore, attachmentToEdit, userStore, appContextStore } = this.state;
        if (taskStore == null || bucketStore == null || conversationStore == null || planStore == null || driveItemStore == null || containerStore == null || userStore == null || appContextStore == null) {
            return ViewRenderState.None;
        }
        // Get critical data from the stores
        const isTasksCriticalDataLoaded = !!(taskStore.getTask(this.props.taskId) && taskStore.getTaskDetails(this.props.taskId));
        const task = taskStore.getTask(this.props.taskId)?.data;
        if (task == null) {
            return ViewRenderState.None;
        }
        const bucket = task.bucketId != null ? bucketStore.getBucket(task.bucketId) : null;
        const isBucketDataLoaded = task.bucketId == null || bucket != null && bucket.status !== StoreEntityStatus.Unfetched;
        const isCriticalDataReady = isTasksCriticalDataLoaded && isBucketDataLoaded;
        // Get non-critical data from the stores
        const plan = planStore.getPlan(task.planId).data;
        const taskConversation = task.conversationThreadId ? plan && conversationStore.getGroupConversationThread(plan.container.externalId, task.conversationThreadId).status : null;
        const containerExternalId = plan ? plan.container.externalId : null;
        const storeContainer = containerExternalId != null ? containerStore.getContainer(containerExternalId).data : null;
        const storeGroupContainer = storeContainer?.containerType === ContainerType.Group ? storeContainer : null;
        const groupDetails = storeGroupContainer?.unifiedGroupDetails ?? null;
        const driveItems = groupDetails?.id && attachmentToEdit?.url ? driveItemStore.getDriveItemThumbnail(groupDetails.id, attachmentToEdit.url).status : null;
        const user = userStore.getUserById(appContextStore.getAppContext().userId || "").status;
        const isUserIdFetched = !isStatusFetchingOrUnfetched(user);
        const isCommentsFetched = taskConversation ? !isStatusFetchingOrUnfetched(taskConversation) : true;
        const isAttachmentFetched = driveItems ? !isStatusFetchingOrUnfetched(driveItems) : true;
        if (isUserIdFetched && isCommentsFetched && isAttachmentFetched && isCriticalDataReady) {
            return ViewRenderState.All;
        }
        if (isCriticalDataReady) {
            return ViewRenderState.Critical;
        }
        return ViewRenderState.None;
    }
    /**
     * Render the toast container.
     * Needs to be rendered in TaskEditorContainer to ensure the screen reader reads the alerts while the task editor is open.
     */ renderToastContainer() {
        return /*#__PURE__*/ React.createElement(Suspense, {
            fallback: /*#__PURE__*/ React.createElement(React.Fragment, null)
        }, /*#__PURE__*/ React.createElement(LazyToastContainer, this.props));
    }
    /**
     * Returns the list of file suggestions after removing suggestions that are already attached
     * @param task The task to get deduped suggestions for
     */ getDedupedFileSuggestions(task) {
        const { intelligenceStore, taskStore } = this.state;
        if (taskStore == null || intelligenceStore == null) {
            return null;
        }
        const suggestions = intelligenceStore.getFileSuggestions(task.id);
        if (suggestions == null) {
            return null;
        }
        const taskDetails = taskStore.getTaskDetails(task.id);
        if (taskDetails == null) {
            return suggestions;
        }
        return suggestions.filter((suggestion)=>taskDetails.references[suggestion.url] == null && taskDetails.references[suggestion.webUrl] == null);
    }
    /**
     * Add the creation post to the task comments thread
     * @param taskComments The comment thread for the associated task
     */ addCreationActivityFeedPost(taskComments) {
        const { task, userStore } = this.state;
        if (task == null || userStore == null) {
            return taskComments;
        }
        const userByIdGetter = (id)=>userStore.getUserById(id).data;
        const creationPost = generateFakeCreationPost(task, userByIdGetter);
        const thread = [
            creationPost
        ];
        if (taskComments == null) {
            return thread;
        } else {
            return thread.concat(taskComments);
        }
    }
    /**
     * Render the delete task dialog
     */ renderDeleteRecurringTaskDialog(plan) {
        if (!this.state.isDeleteRecurringTaskDialogOpen) {
            return null;
        }
        if (plan == null) {
            return null;
        }
        return /*#__PURE__*/ React.createElement(ThemeContext.Consumer, null, (theme)=>{
            const dialogStyles = getPlannerAppDialogStyles(theme);
            const dialogContentStyles = getPlannerAppDialogContentStyles(theme);
            return /*#__PURE__*/ React.createElement(DeleteRecurringTaskDialog, {
                dialogClassName: "plannerAppDialog",
                styles: dialogStyles,
                dialogContentStyles: dialogContentStyles,
                onDismiss: this.closeDeleteRecurringTaskDialog,
                deleteTask: this.onDeleteTask,
                deleteTaskSeries: this.onDeleteTaskSeries,
                layerProps: this.props.layerProps,
                focusTrapZoneProps: this.props.focusTrapZoneProps
            });
        });
    }
    /**
     * Fetches associated metadata for recommendations and sends the feedback to 3S
     * @param entityId The associated entityId
     * @param fileSuggestion The file suggestion to send feedback for
     * @param traceSignalType The trace signal type
     * @param traceSignalData The associated trace signal data
     */ async sendFileRecommendationFeedback(entityId, fileSuggestion, traceSignalType, traceSignalData) {
        const { intelligenceStore } = this.state;
        const { traceLogger } = this.props.loggers;
        if (intelligenceStore == null) {
            return;
        }
        const metadata = intelligenceStore.getFileSuggestionsMetadata(entityId);
        if (metadata == null) {
            traceLogger.logTrace(0x1e4142d3 /* tag_4qult */ , TraceLevel.Warning, `User interacted with a file recommendation but no metadata found in store [EntityId=${entityId}]`);
            return;
        }
        if (!fileSuggestion.referenceId) {
            traceLogger.logTrace(0x1e4142d2 /* tag_4quls */ , TraceLevel.Warning, `User submitted feedback for file recommendation but referenceId was falsey. [cv=${metadata.cv}][traceId=${metadata.traceId}][logicalId=${metadata.logicalId}]`);
            return;
        }
        const intelligenceActionCreator = await this.props.actionCreatorProviders.intelligence();
        intelligenceActionCreator.sendFileRecommendationFeedback(metadata.cv, metadata.traceId, fileSuggestion.referenceId, metadata.logicalId, traceSignalType, traceSignalData);
    }
    /**
     * Helper method to sanitize a give task details' note content and return a TaskDetails with sanitized content
     * If no note content is available, return original task details
     * @param taskDetails The original task details before sanitization
     */ sanitizeTaskDetailsNoteContent(taskDetails) {
        const updatedNoteContent = taskDetails.notes?.content;
        const sanitizedNoteContent = updatedNoteContent ? sanitizeContent(updatedNoteContent) : null;
        const updatedTaskDetailsWithSanitizedNoteContent = sanitizedNoteContent ? taskDetails.setProperty("notes", {
            content: sanitizedNoteContent,
            contentType: ContentType.HTML
        }) : taskDetails;
        return updatedTaskDetailsWithSanitizedNoteContent;
    }
    /**
     * Callback for add or edit attachment file on task details
     * @param originalAttachment Original or unedited attachment
     * @param updatedAttachment Updated attachment with changes
     */ async onUpsertAttachment(originalAttachment, updatedAttachment) {
        const { actionCreatorProviders } = this.props;
        const { task, taskStore } = this.state;
        if (task == null || taskStore == null) {
            return;
        }
        const currentTaskDetails = taskStore.getTaskDetails(task.id);
        if (currentTaskDetails == null) {
            return;
        }
        let updatedTaskDetails;
        const lowestPriorityAttachment = currentTaskDetails.getReferenceWithLowestPriority();
        let newPreviewPriority;
        if (lowestPriorityAttachment != null) {
            newPreviewPriority = generateOrderHintBetween(null, lowestPriorityAttachment.previewPriority);
        } else {
            newPreviewPriority = generateOrderHintBetween(null, null);
        }
        if (originalAttachment == null) {
            // This means that we are adding a new attachment
            updatedAttachment = updatedAttachment.setProperty("previewPriority", newPreviewPriority);
            updatedTaskDetails = currentTaskDetails.addAttachment(updatedAttachment);
            if (updatedTaskDetails.previewType === PreviewType.Automatic) {
                updatedTaskDetails = updatedTaskDetails.setProperty("previewType", PreviewType.Reference);
            }
        } else {
            // This means that we want to replace the attachment
            if (originalAttachment.url !== updatedAttachment.url) {
                // Attachment Url has changed then consider it as a new attachment and set a new preview priority
                updatedAttachment = updatedAttachment.setProperty("previewPriority", newPreviewPriority);
            }
            updatedTaskDetails = currentTaskDetails.replaceAttachment(originalAttachment, updatedAttachment);
            this.logUsage(EngagementTelemetryConstants.EditAttachment, {
                entityId: currentTaskDetails.id
            });
        }
        const taskActionCreator = await actionCreatorProviders.basicTask();
        taskActionCreator.updateTaskDetails(currentTaskDetails, updatedTaskDetails);
        // Ensure the preview for the new attachment in the store
        // taskActionCreator.ensureTaskReferencePreviewThumbnail(updatedTaskDetails, updatedAttachment);
        // Close all child dialogs on save
        this.onEditorChildDialogDismiss();
    }
    /**
     * Render the "Onedrive picker" control
     * @param taskDetails Task details object
     * @param groupDetails Group Details object
     */ renderOnedrivePicker(taskDetails, groupDetails) {
        const { actionCreatorProviders } = this.props;
        const { driveItemStore } = this.state;
        if (driveItemStore == null) {
            return null;
        }
        if (this.state.isChildOnedrivePickerOpen) {
            // If task details of group details haven't yet loaded, don't load the onedrive picker
            if (taskDetails == null) {
                return null;
            }
            if (groupDetails == null) {
                return null;
            }
            const groupDriveItems = driveItemStore.getDriveItemsForGroup(groupDetails.id).data;
            const driveUrl = groupDetails.getWorkloadUrl(GroupWorkLoad[GroupWorkLoad.Documents]);
            if (driveUrl == null) {
                return null;
            }
            const loadFilesStatusCode = driveItemStore.getDriveItemsForGroup(groupDetails.id).status;
            let showErrorDialog = false;
            let oneDriveStrings = getOneDrivePickerStrings();
            if (groupDriveItems == null && failedStoreEntityStatuses.includes(loadFilesStatusCode)) {
                // Set to show the error loading screen with following message based on error
                let filesLoadErrorMessage;
                if (loadFilesStatusCode === StoreEntityStatus.Inaccessible) {
                    filesLoadErrorMessage = Strings.OneDrivePickerForAttachments_FileLoadErrorAccessDeniedMessage;
                } else {
                    filesLoadErrorMessage = Strings.OneDrivePickerForAttachments_FileLoadErrorGenericMessage;
                }
                showErrorDialog = true;
                oneDriveStrings = {
                    ...oneDriveStrings,
                    loadingErrorMessage: filesLoadErrorMessage
                };
            }
            const oneDrivePickerProps = {
                addDriveItemAsAttachment: this.onAddDriveItemAsAttachment,
                isDriveItemAnAttachment: this.isDriveItemAnAttachment,
                groupDriveItemsByFolder: groupDriveItems ?? {},
                onDismiss: this.onEditorChildDialogDismiss,
                onFolderDrillDown: async (folderId)=>{
                    const groupActionCreator = await actionCreatorProviders.groups();
                    groupActionCreator.fetchDriveItemsInFolder(groupDetails.id, folderId);
                },
                alertUserAddAttachmentError: async ()=>{
                    const notificationActionCreator = await this.props.actionCreatorProviders.notification();
                    notificationActionCreator.enqueueHiddenToast("SharedComponents_TaskAddAttachmentErrorToastTitle");
                },
                showErrorLoadingFiles: showErrorDialog,
                showRetryOnError: true,
                onRetry: async ()=>{
                    this.logUsage("RetryLoadOdspPicker", {
                        groupId: groupDetails.id
                    });
                    const groupActionCreator = await actionCreatorProviders.groups();
                    groupActionCreator.fetchDriveItemsInFolder(groupDetails.id, null);
                },
                oneDrivePickerStrings: oneDriveStrings
            };
            return /*#__PURE__*/ React.createElement(OneDrivePickerForAttachments, {
                focusTrapZoneProps: this.props.focusTrapZoneProps,
                oneDrivePickerProps: oneDrivePickerProps
            });
        } else {
            return null;
        }
    }
    /**
     * Check whether this task needs to get approval detail information
     */ areApprovalDetailsNeeded() {
        const { task, taskStore } = this.state;
        if (task == null || !task.id || taskStore == null) {
            return false;
        }
        const { flights } = this.props.configProvider();
        if (!flights.EnableMandatoryApprovals) {
            return false;
        }
        const taskDetails = taskStore.getTaskDetails(task.id);
        const isApprovalRequired = taskDetails?.completionRequirements?.approvalRequirement?.isApprovalRequired ?? false;
        const hasApproval = !!taskDetails?.approval;
        // we fetch approval detail when: 1 ECS is on 2 task approval is required 3 task has approval
        return isApprovalRequired && hasApproval;
    }
    /**
     * get the current user role in approval data
     */ getUserRole() {
        const { task, taskStore, appContextStore } = this.state;
        if (!task || !taskStore || !appContextStore) {
            return ApprovalUserRole.NotSpecified;
        }
        const taskApprovalDetails = taskStore.getTaskApprovalDetails(task.id);
        const data = taskApprovalDetails?.data;
        const requester = data?.owner;
        const requestedApproverIds = data?.approvers;
        const currentUserId = appContextStore.getAppContext().userId;
        if (!currentUserId) {
            return ApprovalUserRole.NotSpecified;
        } else if (requestedApproverIds?.includes(currentUserId)) {
            return ApprovalUserRole.Approver;
        } else if (requester?.id === currentUserId) {
            return ApprovalUserRole.Owner;
        } else {
            return ApprovalUserRole.NotSpecified;
        }
    }
    /**
     * Returns whether the plan title navigation link should be enabled
     * Currently the plan title nav link is only enabled in aggregate views
     */ shouldEnablePlanTitleNavLink() {
        const { task, planStore } = this.state;
        if (planStore == null || task == null) {
            return false;
        }
        const { storeProviders } = this.props;
        const routeStore = storeProviders.route();
        const currentSubRouteType = routeStore.getCurrentSubRouteType();
        // Non User views are non-aggregate views
        if (currentSubRouteType !== SubRouteType.MyTasks && currentSubRouteType !== SubRouteType.MyDay) {
            return false;
        }
        const routingDetails = extractMyTasksRoutingDetailsFromSubRoute(routeStore.getCurrentSubRoute(), currentSubRouteType);
        // Private tasks and Flagged emails are non-aggregate views
        if (routingDetails?.pivotType === MyTasksViewPivotType.PrivateTasks || routingDetails?.pivotType === MyTasksViewPivotType.FlaggedEmails) {
            return false;
        }
        const plan = planStore.getPlan(task.planId).data;
        if (!plan) {
            return false;
        }
        return true;
    }
    shouldEnableBasicPlanTaskChatMentions() {
        const { task } = this.state;
        const { configProvider } = this.props;
        const { flights } = configProvider();
        // disable chat for todo tasks and project tasks
        const isTodoTask = task?.kind === TaskKind.Todo;
        const isProjectTask = task?.isProjectTask();
        return flights.EnableBasicPlanTaskChatMentions && !isTodoTask && !isProjectTask;
    }
    renderLoadingSpinner(theme) {
        const { suspenseStyle } = this.props;
        const emptyModal = getLoadingSpinnerStyles(theme, suspenseStyle);
        return /*#__PURE__*/ React.createElement("div", {
            className: mergeStyles(emptyModal)
        }, /*#__PURE__*/ React.createElement("div", {
            className: "page-spinner"
        }, /*#__PURE__*/ React.createElement(Spinner, {
            size: SpinnerSize.large,
            className: "spinner",
            styles: appSpinnerStyles
        })));
    }
    /**
     * Initializes an instance of the task editor wrapper
     */ constructor(props, context){
        super(props, context), /**
     * Ref to be used to scroll to file suggestions container
     */ this.fileSuggestionsContainerRef = /*#__PURE__*/ React.createRef(), /**
     * Ref to the scrollable container element for scroll preservation during copy operations
     */ this.scrollContainerRef = /*#__PURE__*/ React.createRef(), /**
     * Previous plan reference to detect changes
     */ this.prevPlan = null, /**
     * Render the comment thread view control for the task
     * @param taskPermissions The current task permissions
     * @param collapseThreshold Number of posts after which the thread should be collapsed
     */ this.renderCommentThreadViewControl = (taskPermissions, collapseThreshold)=>{
            const { actionCreatorProviders, configProvider } = this.props;
            const { task, planStore, containerStore, conversationStore, userStore, appContextStore, permissionsStore } = this.state;
            if (task == null || planStore == null || containerStore == null || conversationStore == null || userStore == null || appContextStore == null || permissionsStore == null) {
                return null;
            }
            const plan = planStore.getPlan(task.planId).data;
            if (!plan || plan.container.containerType !== ContainerType.Group) {
                return null;
            }
            const storeContainer = containerStore.getContainer(plan.container.externalId).data;
            if (!storeContainer) {
                return null;
            }
            const storeGroupContainer = storeContainer;
            const groupDetails = storeGroupContainer.unifiedGroupDetails ?? null;
            // Yammer Group does not have exchange
            if (groupDetails == null || groupDetails.isYammerGroup()) {
                return null;
            }
            const taskConversation = task.conversationThreadId ? conversationStore.getGroupConversationThread(plan.container.externalId, task.conversationThreadId) : null;
            const taskComments = taskConversation?.data ?? [];
            const conversation404 = taskConversation?.status === StoreEntityStatus.NotFound;
            let showLoadingError = false;
            if (taskConversation?.status === StoreEntityStatus.Fetching) {
                // Block ui until the conversation thread has been fetched
                return null;
            }
            if (taskComments != null && taskConversation?.status === StoreEntityStatus.UnableToFetch) {
                showLoadingError = true;
            }
            const currentUserId = appContextStore.getAppContext().userId;
            const currentUser = userStore.getUserById(currentUserId || "").data;
            if (!currentUser) {
                return null;
            }
            const taskFeed = taskComments != null ? this.addCreationActivityFeedPost(taskComments) : [];
            const subject = getTaskCommentEmailSubject(task.title);
            const commentCallbacks = {
                getUserPhoto: (userId)=>userStore.getUserPhoto(userId).data,
                getUserByEmail: (email)=>userStore.getUserByEmail(email).data,
                postComment: async (newPost)=>{
                    this.logUsage("PostComment", {
                        newConversation: String(task.conversationThreadId == null),
                        conversation404: String(conversation404)
                    });
                    const unifiedGroupActionCreator = await actionCreatorProviders.groups();
                    if (task.conversationThreadId == null || conversation404) {
                        unifiedGroupActionCreator.createConversationThread(plan.container.externalId, newPost, subject, task);
                    } else {
                        unifiedGroupActionCreator.postReplyInConversationThread(plan.container.externalId, task.conversationThreadId, newPost, task, subject);
                    }
                },
                onRenderPersona: this.onRenderPersona,
                getConversationThreadPost: async (postId)=>{
                    const unifiedGroupActionCreator = await actionCreatorProviders.groups();
                    unifiedGroupActionCreator.fetchConversationThreadPost(plan.container.externalId, task.conversationThreadId || "", postId);
                }
            };
            const timestamp = new Date();
            const plannerWebRootUrl = configProvider().settings.serviceConfigurations.plannerWeb.hostname;
            const linkRoot = getRedirectRootUrl(plannerWebRootUrl, currentUser.mail);
            const taskLink = generateEntityLink(linkRoot, task, NotificationType.Comment, NotificationChannel.GroupMailbox, timestamp);
            const planLink = plan ? generateEntityLink(linkRoot, plan, NotificationType.Comment, NotificationChannel.GroupMailbox, timestamp) : "";
            const newCommentDisplayMode = this.shouldEnableBasicPlanTaskChatMentions() ? NewCommentDisplayMode.Hidden : !taskPermissions.canEdit ? NewCommentDisplayMode.Disabled : NewCommentDisplayMode.Normal;
            return /*#__PURE__*/ React.createElement(CommentThreadView, {
                commentThread: taskFeed,
                commentViewCallbacks: commentCallbacks,
                collapseThreshold: collapseThreshold,
                currentUser: currentUser,
                showLoadingError: showLoadingError,
                taskLink: taskLink,
                task: task,
                planLink: planLink,
                planTitle: plan ? plan.title : "",
                newCommentDisplayMode: newCommentDisplayMode
            });
        }, /** Callback triggered when opening the chat panel */ this.onOpenChatPanel = async ()=>{
            this.fetchChatMessages();
            // Autofocus the chat input when the opening chat panel
            this.setState({
                focusChatOnInit: true
            });
        }, /** Fetches chat messages for the task */ this.fetchChatMessages = async ()=>{
            if (!this.shouldEnableBasicPlanTaskChatMentions()) {
                return;
            }
            const chatActionCreator = await this.props.actionCreatorProviders.chat();
            chatActionCreator.fetchInitialChatMessages(PlannerChatThreadType.Task, this.props.taskId);
        }, /** Renders the chat thread view control */ this.renderChatThreadViewControl = ()=>{
            const { task, chatStore, appContextStore, userStore, focusChatOnInit } = this.state;
            if (!task || !chatStore || !appContextStore || !userStore) {
                return null;
            }
            const { data: chatMessages, status } = chatStore.getChatMessagesForTask(task.id);
            const currentUserId = appContextStore.getAppContext().userId;
            if (!currentUserId) {
                return null;
            }
            const isLoading = task.hasChat && (status.state === StoreEntityState.Absent || status.isFetching);
            // Don't allow loading more messages if in UnableToFetch state, because this can cause infinite loading loop
            const hasMoreMessages = chatStore.getFetchMessageNextLinkByEntityId(task.id) != null && status.state !== StoreEntityState.UnableToFetch;
            const prioritizedUserIdsForMentionSuggestions = Object.keys(task.assignments);
            return /*#__PURE__*/ React.createElement(ChatThreadView, {
                chatMessages: chatMessages,
                isLoading: isLoading,
                hasMore: hasMoreMessages,
                currentUserId: currentUserId,
                callbacks: {
                    onLoadMore: this.loadMoreChatMessages,
                    getUserById: this.getUserById,
                    getMentionableUsers: this.getMentionableUsers,
                    onOpenAtMentionPicker: this.onOpenAtMentionPicker,
                    onDeleteMessage: this.onDeleteMessage,
                    onEditMessage: ()=>{},
                    onSendMessage: this.onSendMessage,
                    getUserPhoto: this.getUserPhoto,
                    onRenderPersona: this.onRenderPersona
                },
                prioritizedUserIdsForMentionSuggestions: prioritizedUserIdsForMentionSuggestions,
                focusOnInit: focusChatOnInit
            });
        }, /** Method to render the form response view dialog */ this.renderFormResponseViewDialog = ()=>{
            if (!this.state.isFormResponseViewDialogOpen || !this.state.formToOpen) {
                return null;
            }
            const { task, driveItemStore } = this.state;
            if (task == null || driveItemStore == null) {
                return null;
            }
            const groupId = this.getCurrentGroupContainer()?.unifiedGroup.id ?? "";
            const formResponse = driveItemStore?.getGroupFormResponse(task.id, this.state.formToOpen.formId).data;
            const getThumbnail = (url)=>{
                return driveItemStore.getDriveItemThumbnail(groupId, url).data;
            };
            return /*#__PURE__*/ React.createElement(ThemeContext.Consumer, null, (theme)=>{
                const dialogStyles = getPlannerAppDialogStyles(theme);
                const dialogContentStyles = getPlannerAppDialogContentStyles(theme);
                return /*#__PURE__*/ React.createElement(React.Suspense, {
                    fallback: /*#__PURE__*/ React.createElement(React.Fragment, null)
                }, /*#__PURE__*/ React.createElement(LazyFormResponseViewDialog, {
                    onSubmitAnotherResponseClick: ()=>this.onOpenIframeFormRequirementDialog(this.state.formToOpen),
                    styles: dialogStyles,
                    dialogContentStyles: dialogContentStyles,
                    onDismiss: this.onEditorChildDialogDismiss,
                    formWithQA: formResponse ? formResponse : null,
                    getThumbnail: getThumbnail
                }));
            });
        }, /**
     * Render the assignment control
     * @param taskPermissions The current task permissions
     */ this.renderAssignmentControl = (taskPermissions, containerPermissions)=>{
            const { task, appContextStore, permissionsStore, planStore, userStore } = this.state;
            if (task == null || userStore === null || permissionsStore === null || planStore === null || appContextStore === null) {
                return null;
            }
            const assignedToUserIds = task.getAssigneeIds();
            const assignees = map(assignedToUserIds, (userId)=>{
                let user = userStore.getUserById(userId).data;
                if (!user) {
                    // The user id was not found in the store
                    user = UnknownUser.getUnknownUser(userId);
                }
                return user;
            });
            const plan = planStore.getPlan(task.planId);
            const canUserAccessPlanViaSharedContainers = plan?.data != null && permissionsStore ? permissionsStore.canAccessPlanViaSharedContainers(plan.data?.sharedWithContainers) && !permissionsStore.canAccessPlanViaHostContainer(plan.data) : false;
            const canUserAssignTasks = containerPermissions.canAddMembers && taskPermissions.canEdit && taskPermissions.canAssign || canUserAccessPlanViaSharedContainers;
            const { clientBasicLimits } = this.props.configProvider().settings;
            const { maxDisplayablePersonasTaskDetails, maxTaskAssignees } = clientBasicLimits;
            return /*#__PURE__*/ React.createElement(TaskAssignmentControl, {
                layout: LayoutStyle.Compact,
                assignees: assignees,
                assignedAgent: this.getCurrentlyAssignedAgent(),
                availableAgents: this.getAvailableAgents(),
                currentUserId: appContextStore.getAppContext().userId || "",
                canEditAssignments: taskPermissions.canAssign,
                canEditAccessPermissions: containerPermissions.canAddMembers,
                canGiveAccessToGuests: permissionsStore.getUserPermissions().canAddGuests,
                canAssignToResourcesWithoutAccess: containerPermissions.canAddMembers,
                canAccessPlanViaSharedContainerOnly: canUserAccessPlanViaSharedContainers,
                minQueryTermLength: MinCharsNeededToSearch,
                maxDisplayablePersonas: maxDisplayablePersonasTaskDetails,
                maxAssignees: maxTaskAssignees,
                addIconPlacement: AddIconPlacement.BeforeAssignees,
                strings: {
                    assignControlIconTitle: Strings.TaskAssignments_AssignControlLauncherText,
                    assignControlLauncherText: Strings.TaskAssignments_AssignControlLauncherText,
                    assignControlAssignToMeButtonText: Strings.TaskAssignments_AssignToMeLabel,
                    readonlyFieldPermissionsTooltip: Strings.SharedComponents_ReadonlyFieldPermissionsTooltip,
                    assignControlAriaLabelSuffix: Strings.TaskAssignments_AssignControlAriaLabelSuffix,
                    assignControlAriaLabelPrefix: Strings.TaskAssignments_AssignControlAriaLabelPrefix,
                    assignControlAriaLabelPrefixNoAssignees: Strings.TaskAssignments_AssignControlAriaLabelPrefixNoAssignees,
                    assignControlListSeparator: Strings.TaskAssignments_AssignControlAriaLabelListSeparator,
                    assignControlAriaLabelConstruction: Strings.TaskAssignments_AssignControlAriaLabelConstruction,
                    assignmentFilterPlaceholderText: Strings.TaskAssignments_AssignFilterPlaceholder,
                    assignmentFilterAriaLabel: Strings.TaskAssignments_AssignFilterPlaceholder,
                    noResultsFoundText: Strings.TaskAssignments_NoResultsFoundText,
                    giveAccessTooltipText: Strings.TaskAssignments_GiveAccessButtonAriaLabel,
                    unassignUserButtonText: Strings.TaskAssignments_UnassignUserButtonAriaLabel,
                    assignedSectionHeader: Strings.TaskAssignments_AssignedSectionHeader,
                    queryLoadingText: Strings.TaskAssignments_LoadingText,
                    suggestedSectionHeader: Strings.TaskAssignments_SuggestedSectionHeader,
                    personaRoleGuest: Strings.TaskAssignments_PersonaRoleGuest,
                    personaRoleNonMember: Strings.TaskAssignments_PersonaRoleNonMember,
                    personaCompoundRole: Strings.TaskAssignments_PersonaCompoundSubText,
                    suggestedAssigneeMostAvailableUser: Strings.TaskAssignments_SuggestedAssigneeMostAvailableUser,
                    suggestedAssigneePlanTopAssignee: Strings.TaskAssignments_SuggestedAssigneePlanTopAssignee,
                    suggestedAssigneeSimilarTitle: Strings.TaskAssignments_SuggestedAssigneeSimilarTitle,
                    suggestedAssigneeUserTopAssignee: Strings.TaskAssignments_SuggestedAssigneeUserTopAssignee
                },
                onRenderPersona: this.onRenderPersona,
                getUsersWithPlanAccess: this.getAssigneePoolForPlan,
                // Assignee Suggestions not in current scope
                getZeroTermQueryResults: ()=>[],
                onAssignUser: this.onAssignUser,
                onUnassignUser: this.onUnassignUser,
                onAssignAgent: this.onAssignAgent,
                onUnassignAgent: this.onUnassignAgent,
                onGivePlanAccess: this.giveUserPlanAccess,
                onOpenAssignCallout: this.onOpenAssignCallout,
                onUserSearchQueryChanged: canUserAssignTasks ? this.fetchUsersByKeyword : undefined,
                getUsersByKeyword: canUserAssignTasks ? this.getUsersByKeyword : undefined,
                isUsersByKeywordDataReady: canUserAssignTasks ? this.isUsersByKeywordDataReady : undefined,
                getUserPhoto: this.getUserPhoto,
                showAssigneeLimitError: this.showAssigneeLimitError,
                alertUser: this.tenantSearchAlertUser,
                shouldHideFacepileBar: false,
                agentsInfoContentMap: plan.data ? this.getAgentsInfoContentMap(plan.data.isMeetingPlan()) : undefined
            });
        }, /**
     * Callback to get users by keyword
     * @param keyword search keyword
     */ this.getUsersByKeyword = (keyword)=>{
            return this.state.userStore?.getUsersByKeyword(keyword).data ?? [];
        }, /**
     * Callback to check if users by keyword data is present in the store
     * @param keyword search keyword
     */ this.isUsersByKeywordDataReady = (keyword)=>{
            return this.state.userStore?.getUsersByKeyword(keyword).status === StoreEntityStatus.Present;
        }, /**
     * Callback to get users with keyword
     * @param keyword search keyword
     */ this.fetchUsersByKeyword = async (keyword)=>{
            const userActionCreator = await this.props.actionCreatorProviders.user();
            userActionCreator.fetchUsersByKeyword(keyword);
        }, /**
     * On open error dialog callback
     * @param errorHeader Header for the error dialog
     * @param errorMessage Error message to show in the dialog
     * @param correlationId Possible correlation id to show
     */ this.onOpenErrorDialog = async (errorHeader, errorMessage, correlationId)=>{
            const dialogActionCreator = await this.props.actionCreatorProviders.modal();
            dialogActionCreator.openInfoModal(errorHeader, errorMessage, Strings.SharedComponents_ButtonNameOK, undefined, correlationId);
        }, /**
     * Callback to open assignee limit error dialog
     */ this.showAssigneeLimitError = async ()=>{
            const modalAC = await this.props.actionCreatorProviders.modal();
            modalAC.openAssigneeLimitErrorModal(this.props.taskId);
        }, /**
     * Callback when Assignment control opens the callout to pick/update assignments.
     */ this.onOpenAssignCallout = async ()=>{
            this.fetchUsersAndSharedContainers();
        }, this.fetchUsersAndSharedContainers = async ()=>{
            const { actionCreatorProviders } = this.props;
            const { task, planStore, userStore, driveItemStore } = this.state;
            if (task == null || planStore == null || userStore == null || driveItemStore == null) {
                return;
            }
            const plan = planStore.getPlan(task.planId).data;
            if (plan == null) {
                return;
            }
            // If users haven't been fetched for group, fetch them
            if (userStore.getContainerUsers(plan.container.externalId).data == null) {
                const userActionCreator = await actionCreatorProviders.user();
                userActionCreator.fetchUsersForContainer(plan.container);
            }
            // Fetch data for any shared containers, if they haven't been fetched already
            if (plan.sharedWithContainers != null) {
                for (const sharedWithContainer of plan.sharedWithContainers){
                    if (sharedWithContainer.containerType === ContainerType.DriveItem && driveItemStore.getDriveItem(sharedWithContainer.externalId).data == null) {
                        const driveItemActionCreator = await actionCreatorProviders.driveItem();
                        driveItemActionCreator.fetchDriveItem(sharedWithContainer.externalId);
                    }
                }
            }
        }, /**
     * Gets the Map of agent ids to info content for the agents
     * @param isMeetingPlan Whether the current plan is a meeting plan
     */ this.getAgentsInfoContentMap = (isMeetingPlan)=>{
            const agentsInfoContentMap = {};
            const agents = this.getAvailableAgents();
            agents.forEach((agent)=>{
                if (agent.id === getProjectManagerAgent().id && isMeetingPlan) {
                    agentsInfoContentMap[agent.id] = Strings.MeetingPlans_ProjectManagerLimitedAccessInfo;
                }
            });
            return agentsInfoContentMap;
        }, /**
     * Get the copilot workflow state label for a task
     * @param taskId Id of the task
     */ this.getCopilotWorkflowStatusLabel = ()=>{
            const { storeProviders, taskId } = this.props;
            const { taskStore, planStore, permissionsStore } = this.state;
            if (!taskStore || storeProviders.route().getCurrentSubRouteType() !== SubRouteType.BasicPlan) {
                return null;
            }
            const planId = taskStore.getTask(taskId).data?.planId;
            const plan = planId ? planStore?.getPlan(planId).data : null;
            if (!plan || !permissionsStore?.getPlanPermissions(plan)?.canUseAgents) {
                return null;
            }
            const projectManagerState = taskStore.getWorkflowStateForTask(taskId);
            const labelData = getLabelDataByCopilotWorkflowStatus(projectManagerState);
            if (!labelData) {
                return null;
            }
            const { name, index } = labelData;
            return {
                iconProps: plannerLogoOutlineIconProps,
                name,
                index,
                id: index.toString(),
                labelFormatting: LabelConstants.LabelFormattingData[index],
                placeholderText: "",
                isStaticLabel: true
            };
        }, /**
     * Called when user persona card is loaded
     */ this.onRenderPersona = async (user)=>{
            const { userStore } = this.state;
            if (userStore == null) {
                return null;
            }
            // Lazy load thumbnail
            const storeImageUrl = userStore.getUserPhoto(user.id).data;
            if (!storeImageUrl || storeImageUrl === FormerMember.Photo) {
                const userActionCreator = await this.props.actionCreatorProviders.user();
                userActionCreator.fetchPhotosForUsers([
                    user.id
                ]);
            }
        }, /**
     * Callback for when a user is added via the people picker
     * @param userId User to add
     * @param elementToFocusOnDismiss HTML element to focus on when dismissing warning dialog
     * @param onAssignSuccess Callback for when the task assignment is successful
     */ this.onAssignUser = async (userId, elementToFocusOnDismiss, onAssignSuccess)=>{
            const { actionCreatorProviders } = this.props;
            const { task, appContextStore, taskStore } = this.state;
            if (task == null || appContextStore == null || taskStore == null) {
                return;
            }
            const currentUserId = appContextStore.getAppContext().userId;
            if (!currentUserId) return;
            if (task.getAssigneeIds().indexOf(userId) === -1) {
                const updatedTask = task.getClone();
                updatedTask.assignments[userId] = new Assignment(currentUserId, new Date());
                const taskActionCreator = await actionCreatorProviders.basicTask();
                taskActionCreator.assignTask({
                    userId,
                    originalTask: task,
                    updatedTask,
                    elementToFocusOnDismiss,
                    onAssignSuccess
                });
                this.fetchFileSuggestions(updatedTask, taskStore.getTaskDetails(task.id));
            }
        }, /**
     * Callback for when an agent is assigned to the task
     * Assign the task to the agent and assign the user to the task as well
     * @param agentId Id of the agent to assign the task to
     */ this.onAssignAgent = async (agentId)=>{
            const { actionCreatorProviders, storeProviders, taskId, loggers } = this.props;
            const appContextStore = storeProviders.appContext();
            const currentUserId = appContextStore.getAppContext().userId;
            if (!currentUserId) {
                loggers.traceLogger.logTrace(0x1e21c447 /* tag_4i2rh */ , TraceLevel.Error, "currentUserId is null", {
                    taskId,
                    agentId
                });
                return;
            }
            const taskActionCreator = await actionCreatorProviders.basicTask();
            taskActionCreator.assignTaskToAgent(taskId, agentId);
            this.onAssignUser(currentUserId);
        }, /**
     * Callback for when an agent is unassigned from the task
     */ this.onUnassignAgent = async (agentId)=>{
            const { actionCreatorProviders, taskId } = this.props;
            const taskActionCreator = await actionCreatorProviders.basicTask();
            taskActionCreator.unassignTaskFromAgent(taskId, agentId);
        }, /**
     * Get the agent assigned to the task
     */ this.getCurrentlyAssignedAgent = ()=>{
            const { taskStore, agents, permissionsStore, planStore } = this.state;
            const { taskId } = this.props;
            const task = taskStore?.getTask(taskId)?.data;
            const planId = task?.planId;
            const plan = planId ? planStore?.getPlan(planId).data : null;
            const canUseAgents = plan && permissionsStore?.getPlanPermissions(plan).canUseAgents;
            if (!planStore || !permissionsStore || !taskStore || !agents || !(canUseAgents && this.showAgentAssignments())) {
                return null;
            }
            for (const agent of agents){
                const workflowState = taskStore.getWorkflowStateForTask(taskId);
                if (// task is assigned to project manager or project manager is actively working on the task
                agent.id === projectManagerAgentId && [
                    CopilotWorkflowUnifiedState.Queued,
                    CopilotWorkflowUnifiedState.InProgress,
                    CopilotWorkflowUnifiedState.NeedsInfo
                ].includes(workflowState)) {
                    return agent;
                }
            }
            return null;
        }, /**
     * Whether the agent assignment should be shown in the editor
     */ this.showAgentAssignments = ()=>{
            const { storeProviders, configProvider } = this.props;
            const routeStore = storeProviders.route();
            const currentSubRouteType = routeStore.getCurrentSubRouteType();
            const isSettingEnabled = configProvider().settings.GSDInBasicPlanSettings.enablePMAInMyDayAndMyTasksBoardView;
            return currentSubRouteType === SubRouteType.BasicPlan || isSettingEnabled;
        } // Only show project manager assignment in task editor for basic plan / My day / My tasks views
        , /**
     * Get the available agents based on user permissions
     */ this.getAvailableAgents = ()=>{
            const { agents, permissionsStore, planStore, task } = this.state;
            if (task === null || planStore === null || permissionsStore === null) {
                return [];
            }
            const plan = planStore.getPlan(task.planId).data;
            if (plan === null) {
                return [];
            }
            return permissionsStore.getPlanPermissions(plan).canUseAgents && permissionsStore.getUserPermissions().canAssignToCopilot && this.showAgentAssignments() ? agents : [];
        }, /**
     * Callback for when a user is unassigned from this task via the people picker
     */ this.onUnassignUser = async (userId)=>{
            const { actionCreatorProviders } = this.props;
            const { task, taskStore } = this.state;
            if (task == null || taskStore == null) {
                return;
            }
            if (task.getAssigneeIds().indexOf(userId) !== -1) {
                const updatedTask = task.getClone();
                delete updatedTask.assignments[userId];
                const taskActionCreator = await actionCreatorProviders.basicTask();
                taskActionCreator.updateTask(TaskEntitySet.builder().withTask(task).build(), TaskEntitySet.builder().withTask(updatedTask).build());
                this.fetchFileSuggestions(updatedTask, taskStore.getTaskDetails(task.id));
            }
        }, /**
     * Get the assignee pool for the plan
     */ this.getAssigneePoolForPlan = ()=>{
            const { task, planStore, userStore } = this.state;
            if (task == null || planStore == null || userStore == null) {
                return [];
            }
            const plan = planStore.getPlan(task.planId).data;
            const unifiedGroupId = plan ? plan.container.externalId : null;
            return unifiedGroupId ? userStore.getContainerUsers(unifiedGroupId)?.data || [] : [];
        }, /** Gets user data by user ID */ this.getUserById = (userId)=>{
            const { userStore } = this.state;
            if (userStore == null) {
                return {
                    data: null
                };
            }
            return userStore.getUserById(userId);
        }, /**
     * Get the mentionable users for chat
     */ this.getMentionableUsers = ()=>{
            const { storeProviders } = this.props;
            const assignableUsers = this.getAssigneePoolForPlan();
            // Exclude current user
            const appContextStore = storeProviders.appContext();
            const currentUserId = appContextStore.getAppContext().userId;
            return assignableUsers.filter((user)=>user.id !== currentUserId);
        }, /** Callback triggered when the at-mention picker is opened */ this.onOpenAtMentionPicker = ()=>{
            this.fetchUsersAndSharedContainers();
        }, /**
     * Callback for opening the copy task dialog
     */ this.openCopyTaskDialog = async ()=>{
            this.logUsage(EngagementTelemetryConstants.CopyTaskDialogOpened);
            const modalAC = await this.props.actionCreatorProviders.modal();
            modalAC.openCopyTaskModal(this.props.taskId, ()=>{});
        }, /**
     * Callback for opening the move task dialog
     */ this.openMoveTaskDialog = async ()=>{
            this.logUsage(EngagementTelemetryConstants.MoveTaskDialogOpened);
            const modalAC = await this.props.actionCreatorProviders.modal();
            modalAC.openMoveTaskModal(this.props.taskId, ()=>{});
        }, /**
     * Callback for opening the delete recurring task dialog
     */ this.openDeleteRecurringTaskDialog = ()=>{
            this.logUsage(EngagementTelemetryConstants.DeleteRecurringTaskDialogOpened);
            this.setState({
                isDeleteRecurringTaskDialogOpen: true
            });
        }, /**
     * Callback for closing the delete recurring task dialog
     */ this.closeDeleteRecurringTaskDialog = ()=>{
            this.logUsage(EngagementTelemetryConstants.DeleteRecurringTaskDialogClosed);
            this.setState({
                isDeleteRecurringTaskDialogOpen: false,
                currentTaskEditorFocus: TaskEditorDialogFocusOptions.MoreOptionsButton
            });
        }, /**
     * Called when Task Delete is clicked in the TaskDetails dialog
     */ this.onDeleteTask = async ()=>{
            const { actionCreatorProviders } = this.props;
            const { task } = this.state;
            if (task == null) {
                return;
            }
            // Close the dialog
            await this.closeEditor();
            // Delete the task
            const taskActionCreator = await actionCreatorProviders.basicTask();
            taskActionCreator.deleteTask(task.id, task.itemVersion);
        // TODO: PBI #8683832 - After dialog is closed, invoke callback to restore focus
        }, /**
     * Called when Task Delete is clicked in the TaskDetails dialog
     */ this.onDeleteTaskSeries = async ()=>{
            const { actionCreatorProviders } = this.props;
            const { task } = this.state;
            if (task == null) {
                return;
            }
            // Close the editor
            await this.closeEditor();
            // Delete the task series
            const taskActionCreator = await actionCreatorProviders.basicTask();
            taskActionCreator.deleteTaskSeries(task.id);
        // TODO: PBI #8683832 - After dialog is closed, invoke callback to restore focus
        }, /** Helper method to close task editor */ this.closeEditor = async ()=>{
            const { actionCreatorProviders, onClose } = this.props;
            const modalAC = await actionCreatorProviders.modal();
            modalAC.closeModal();
            onClose();
        }, /**
     * Method to open the one drive picker control
     * @param groupId Id of the group backing the drive picker
     */ this.onOpenOnedrivePicker = async (groupId)=>{
            if (groupId == null) {
                return;
            }
            const groupsActionCreator = await this.props.actionCreatorProviders.groups();
            groupsActionCreator.fetchDriveItemsInFolder(groupId, null);
            this.setState({
                isChildOnedrivePickerOpen: true
            });
        }, /**
     * Callback to open the attachment details editor dialog
     * @param attachmentUrl Id of the attachment to edit
     * @param elementToFocusOnDismiss Element to restore focus to on dismissing the dialog
     */ this.openAttachmentEditorDialog = async (attachment, elementToFocusOnDismiss)=>{
            const { actionCreatorProviders } = this.props;
            const { task } = this.state;
            if (task == null) {
                return;
            }
            const modalAC = await actionCreatorProviders.modal();
            modalAC.openTaskAttachmentDetailsModal(task.id, attachment, elementToFocusOnDismiss);
            this.setState({
                attachmentToEdit: attachment
            });
        }, /**
     * Called when a child dialog of the task editor is dismissed. Closes all child dialogs.
     */ this.onEditorChildDialogDismiss = ()=>{
            this.setState({
                isChildOnedrivePickerOpen: false,
                isChildCustomRecurrenceDialogOpen: false,
                attachmentToEdit: null,
                isAddFormRequirementDialogOpen: false,
                isRemoveFormConfirmationDialogOpen: false,
                isFormResponseViewDialogOpen: false,
                formToRemove: null,
                isEditFormDialogOpen: false,
                formToEdit: null,
                isIframeFormRequirementDialogOpen: false,
                formToOpen: null
            });
        }, /**
     * Callback to close the custom recurrence dialog
     */ this.closeFormResponseViewDialog = ()=>{
            this.setState({
                isFormResponseViewDialogOpen: false
            });
        }, /**
     * Converts a file suggestion to an attachment
     * @param suggestion The file suggestion
     */ this.convertSuggestionToAttachment = async (suggestion)=>{
            this.logUsage(EngagementTelemetryConstants.AddAttachment, {
                type: "FileSuggestion",
                entityId: this.props.taskId
            });
            const taskActionCreator = await this.props.actionCreatorProviders.basicTask();
            taskActionCreator.convertSuggestionToAttachment(this.props.taskId, suggestion);
            this.submitFileRecommendationFeedback(suggestion, true);
        }, /**
     * Copies a link to the given file suggestion to the clipboard
     * @param fileSuggestion The file suggestion
     */ this.copyLinkToSuggestion = async (fileSuggestion)=>{
        // TODO: PBI #8680220 Add logic for copying link to file suggestion
        }, /**
     * Callback to handle when file suggestions are seen for a task
     * @param taskId The id of the task file suggestions were seen for
     * @param seen Whether or not the file suggestions were seen
     */ this.onFileSuggestionsSeen = async (taskId, seen)=>{
            const intelligenceActionCreator = await this.props.actionCreatorProviders.intelligence();
            intelligenceActionCreator.setEntityFileSuggestionsSeen(taskId, seen);
        }, /**
     * Copy link to task
     */ this.onCopyLinkToTask = async ()=>{
            const { task } = this.state;
            if (task == null) {
                return;
            }
            const copyData = {
                taskId: task.id
            };
            this.logUsage("copyTaskLinkTaskDetails", copyData);
            const taskActionCreator = await this.props.actionCreatorProviders.basicTask();
            taskActionCreator.copyLinkToTask(task.id);
        }, /**
     * Update task
     */ this.onUpdateTask = async (originalTask, updatedTask)=>{
            const { taskStore } = this.state;
            const originalTaskDetails = taskStore?.getTaskDetails(originalTask.id) ?? undefined;
            const originalTaskFormatData = taskStore?.getTaskboardTaskFormattingCollection(originalTask.id) ?? undefined;
            const originalTaskEntitySet = TaskEntitySet.builder().withTask(originalTask).withDetails(originalTaskDetails).withFormatData(originalTaskFormatData).build();
            const updatedTaskEntitySet = TaskEntitySet.builder().withTask(updatedTask).withDetails(originalTaskDetails).withFormatData(originalTaskFormatData).build();
            const taskActionCreator = await this.props.actionCreatorProviders.basicTask();
            taskActionCreator.updateTask(originalTaskEntitySet, updatedTaskEntitySet);
        }, /**
     * Handler for when a user gives feedback on a file recommendation
     * @param fileSuggestion The file suggestion the user is giving feedback on
     * @param isPositive Whether or not the feedback is positive
     */ this.submitFileRecommendationFeedback = (fileSuggestion, isPositive)=>{
            this.sendFileRecommendationFeedback(this.props.taskId, fileSuggestion, TraceSignalType.Feedback, isPositive ? FeedbackResponseValue.Yes : FeedbackResponseValue.No);
        }, /**
     * Handler for when user interacts with a file recommendation
     * @param fileSuggestion The file rec the user interacted with
     */ this.onOpenFileSuggestionContextMenu = (fileSuggestion)=>{
            this.sendFileRecommendationFeedback(this.props.taskId, fileSuggestion, TraceSignalType.EntityAction, EntityActionEventType.EntityClicked);
        }, /**
     * Update task details
     */ this.onUpdateTaskDetails = async (originalTaskDetails, updatedTaskDetails)=>{
            const taskActionCreator = await this.props.actionCreatorProviders.basicTask();
            // Only sanitize when the notes content actually changed. Re-sanitizing unchanged content
            // can re-wrap HTML (e.g., span accumulation) and produce a spurious notes diff, which the
            const notesChanged = originalTaskDetails.notes?.content !== updatedTaskDetails.notes?.content;
            const detailsToSend = notesChanged ? this.sanitizeTaskDetailsNoteContent(updatedTaskDetails) : updatedTaskDetails;
            taskActionCreator.updateTaskDetails(originalTaskDetails, detailsToSend);
        }, /**
     * Create a new task
     */ this.onCreateTask = async (task)=>{
            const taskActionCreator = await this.props.actionCreatorProviders.basicTask();
            taskActionCreator.createTask(TaskEntitySet.builder().withTask(task).build());
        }, /** Update a plan details */ this.onUpdatePlanDetails = async (originalPlanDetails, updatedPlanDetails)=>{
            const { planStore } = this.state;
            const plan = planStore?.getPlan(originalPlanDetails.id);
            if (plan?.data) {
                const planActionCreator = await this.props.actionCreatorProviders.basicPlan();
                planActionCreator.updatePlan(PlanEntitySet.builder().withPlan(plan.data).withDetails(originalPlanDetails).build(), PlanEntitySet.builder().withPlan(plan.data).withDetails(updatedPlanDetails).build());
            }
        }, /**
     * Fetches file suggestions for current task
     * @param task The task to use for fetching file suggestions
     * @param taskDetails The taskDetails to use for fetching file suggestions
     */ this.fetchFileSuggestions = async (task, taskDetails)=>{
            const { flights } = this.props.configProvider();
            if (flights.EnableFileIntelligence) {
                const taskEntitySet = TaskEntitySet.builder().withTask(task).withDetails(taskDetails ?? undefined).build();
                const intelligenceActionCreator = await this.props.actionCreatorProviders.intelligence();
                intelligenceActionCreator.fetchFileRecommendations(taskEntitySet);
            }
        }, /**
     * Method to open the custom recurrence dialog
     */ this.onOpenCustomRecurrenceDialog = ()=>{
            this.setState({
                isChildCustomRecurrenceDialogOpen: true
            });
        }, /**
     * Method to render the custom recurrence dialog
     */ this.renderCustomRecurrenceDialog = ()=>{
            if (!this.state.isChildCustomRecurrenceDialogOpen) {
                return null;
            }
            const { task } = this.state;
            if (task == null) {
                return null;
            }
            return /*#__PURE__*/ React.createElement(CustomRecurrenceDialog, {
                onDismiss: this.onEditorChildDialogDismiss,
                onUpdateTask: this.onUpdateTask,
                task: task
            });
        }, /**
     * Method to open the form response view dialog
     */ this.onOpenFormResponseViewDialog = async (formToOpen)=>{
            const { task } = this.state;
            if (task == null) {
                return;
            }
            const taskActionCreator = await this.props.actionCreatorProviders.basicTask();
            taskActionCreator.fetchFormResponseForTask(task.id, formToOpen.formId, true);
            this.setState({
                isFormResponseViewDialogOpen: true,
                formToOpen
            });
        }, this.onOpenEditFormDialog = (form)=>{
            this.setState({
                isEditFormDialogOpen: true,
                formToEdit: form
            });
        }, /** Method to render the remove form confirmation dialog */ this.onOpenRemoveFormConfirmationDialog = (formToRemove)=>{
            this.setState({
                isRemoveFormConfirmationDialogOpen: true,
                formToRemove: formToRemove
            });
        }, /**
     * Method to open the add form requirement dialog
     */ this.onOpenAddFormRequirementDialog = ()=>{
            this.setState({
                isAddFormRequirementDialogOpen: true
            });
        }, /**
     * Method to handle onClick on the Go to premium plan
     */ this.onClickGoToPremiumPlan = async (event)=>{
            // This would prevent project from opening in a browser.
            // We would want to keep the task editor dialog open with a no-op on the link click if the task/external context is null
            event.preventDefault();
            this.onNavigateToPlan();
        }, this.onOpenIframeFormRequirementDialog = async (form)=>{
            this.logUsage("FormsIframe Dialog Opened");
            if (form === null) {
                return null;
            }
            const { task } = this.state;
            const modalAC = await this.props.actionCreatorProviders.modal();
            if (task != null) {
                modalAC.openFormsIframeModal(form.formId, form.urlFormId, task.id, this.closeFormResponseViewDialog, true);
                this.setState({
                    isIframeFormRequirementDialogOpen: true,
                    formToOpen: form
                });
            }
        }, this.openAppPoweredLinkInHost = async (externalURL)=>{
            const linksModuleAC = await this.props.actionCreatorProviders.links();
            linksModuleAC.openAppPoweredLinkInHost(externalURL);
        }, this.openAttachmentInHost = async (attachment)=>{
            const linksModuleAC = await this.props.actionCreatorProviders.links();
            linksModuleAC.openAttachmentInHost(attachment);
        }, /** Get the current group container */ this.getCurrentGroupContainer = ()=>{
            const { task } = this.state;
            const { planStore, containerStore } = this.state;
            if (task == null || planStore == null || containerStore == null) {
                return null;
            }
            const plan = planStore.getPlan(task.planId);
            const containerExternalId = plan.data ? plan.data.container.externalId : null;
            const storeContainer = containerExternalId != null ? containerStore.getContainer(containerExternalId).data : null;
            const storeGroupContainer = storeContainer?.containerType === ContainerType.Group ? storeContainer : null;
            return storeGroupContainer;
        }, /**
     * Helper to get the user photo
     * @param userId The user id to get the photo for
     */ this.getUserPhoto = (userId)=>{
            const { userStore } = this.state;
            if (userStore == null) {
                return null;
            }
            return userStore.getUserPhoto(userId).data;
        }, /**
     * Helper to trigger a hidden toast alert regarding the tenant user search results
     * @param numResults The number of results
     */ this.tenantSearchAlertUser = async (numResults)=>{
            const notificationActionCreator = await this.props.actionCreatorProviders.notification();
            notificationActionCreator.enqueueHiddenToast("Toasts_TenantUserSearchResultsToastTitle", [
                numResults.toString()
            ]);
        }, /**
     * Helper to give a user access to a plan by adding them to the container
     * @param userId The user to add
     */ this.giveUserPlanAccess = async (userId)=>{
            const { task, planStore } = this.state;
            if (task == null || planStore == null) {
                return null;
            }
            const plan = planStore.getPlan(task.planId);
            if (plan?.data?.container != null) {
                const userActionCreator = await this.props.actionCreatorProviders.user();
                userActionCreator.addMembersToContainer([
                    userId
                ], plan.data.container);
            }
        }, /**
     * Method to navigate to a plan
     */ this.onNavigateToPlan = async ()=>{
            const { actionCreatorProviders } = this.props;
            const { task, planStore } = this.state;
            const { traceLogger } = this.props.loggers;
            if (task == null) {
                traceLogger.logTrace(0x1e384286 /* tag_4oekg */ , TraceLevel.Warning, `onNavigateToPlan called without task unexpectedly`);
                return;
            }
            if (planStore == null) {
                traceLogger.logTrace(0x1e384285 /* tag_4oekf */ , TraceLevel.Warning, `onNavigateToPlan called without plan store being initialized [taskId=${task.id}]`);
                return;
            }
            const plan = planStore.getPlan(task.planId).data;
            if (plan == null) {
                traceLogger.logTrace(0x1e384284 /* tag_4oeke */ , TraceLevel.Warning, `onNavigateToPlan called without plan [taskId=${task.id}]`);
                return;
            }
            //Log the usage of the deep link click
            this.logUsage(EngagementTelemetryConstants.TaskDeepLinkClicked, {
                taskId: task.id,
                taskCreationSourceType: task.taskCreationSourceType
            });
            let routingDetails;
            switch(plan.getPlanType()){
                case PlanType.Premium:
                    const external = task.creationSource.external;
                    routingDetails = extractPremiumPlanRoutingDetailsFromExternalTaskDetails(external?.externalContextId ?? undefined, external?.contextScenarioId, external?.externalObjectId ?? undefined);
                    break;
                case PlanType.Basic:
                    routingDetails = {
                        planType: PlanType.Basic,
                        planId: plan.id,
                        planName: plan.title
                    };
                    break;
                case PlanType.ToDo:
                    if (plan.id === planStore.getDefaultPlanId() || plan.id === planStore.getFlaggedEmailPlanId()) {
                        routingDetails = {
                            planType: PlanType.ToDo,
                            planId: plan.id,
                            pivotType: plan.id === planStore?.getDefaultPlanId() ? MyTasksViewPivotType.PrivateTasks : MyTasksViewPivotType.FlaggedEmails
                        };
                        break;
                    }
                    routingDetails = {
                        planType: PlanType.ToDo,
                        planId: plan.id,
                        planName: plan.title
                    };
                    break;
                default:
                    traceLogger.logTrace(0x1e392408 /* tag_4osqi */ , TraceLevel.Warning, `onNavigateToPlan called for an unexpected plan type [planType=${plan.getPlanType()}][taskId=${task.id}]`);
                    return;
            }
            if (routingDetails == null) {
                traceLogger.logTrace(0x1e392707 /* tag_4os2h */ , TraceLevel.Warning, `onNavigateToPlan called without route details unexpectedly [taskId=${task.id}]`);
                return;
            }
            this.onDismissTaskEditorDialog(task.id);
            const unoNavigationActionCreator = await actionCreatorProviders.routing();
            unoNavigationActionCreator.navigateToPlan(routingDetails);
        }, /**
     * On dismiss task editor dialog
     * Check whether there is unpublished approval request, if so, render dialog to confirm sending the request or not.
     * If user Sends request or discards, the editor should be closed. If user click the x button, the editor should not be closed.
     * @param taskId The task id
     */ this.onDismissTaskEditorDialog = async (taskId)=>{
            if (this.state.selectedApprover) {
                this.setState({
                    isCheckApprovalDialogOpen: true
                });
            } else {
                await this.closeEditor();
            }
        }, /**
     * Copy the link for given attachment
     * @param attachment Attachment to copy link for
     */ this.onCopyAttachmentLink = async (attachment)=>{
        // TODO: PBI #8683851 - Add logic for copying link for attachment
        }, /**
     * Method to add the drive item as an attachment
     * @param driveItemToAdd The drive item to add as attachment
     */ this.onAddDriveItemAsAttachment = (driveItemToAdd)=>{
            const { task, planStore, containerStore } = this.state;
            if (task == null || planStore == null || containerStore == null) {
                return;
            }
            const plan = planStore.getPlan(task.planId).data;
            if (!plan || plan.container.containerType !== ContainerType.Group) {
                return;
            }
            const storeContainer = containerStore.getContainer(plan.container.externalId).data;
            if (!storeContainer) {
                return;
            }
            const storeGroupContainer = storeContainer;
            const groupDetails = storeGroupContainer.unifiedGroupDetails ?? null;
            const driveUrl = groupDetails?.getWorkloadUrl(GroupWorkLoad[GroupWorkLoad.Documents]);
            if (driveUrl) {
                const selectedAttachment = Attachment.builder.attachmentFromDriveItem(driveUrl, driveItemToAdd).build();
                this.onUpsertAttachment(null, selectedAttachment);
                this.logUsage(EngagementTelemetryConstants.AddAttachment, {
                    type: "SharePoint",
                    entityId: task.id
                });
            }
        }, /**
     * Method to check if the drive item is an attachment on the task
     * @param driveItem The drive item to check is attachment
     */ this.isDriveItemAnAttachment = (driveItem)=>{
            const { task, planStore, containerStore, taskStore } = this.state;
            if (task == null || planStore == null || containerStore == null || taskStore == null) {
                return false;
            }
            const plan = planStore.getPlan(task.planId).data;
            if (!plan || plan.container.containerType !== ContainerType.Group) {
                return false;
            }
            const storeContainer = containerStore.getContainer(plan.container.externalId).data;
            if (!storeContainer) {
                return false;
            }
            const storeGroupContainer = storeContainer;
            const groupDetails = storeGroupContainer.unifiedGroupDetails ?? null;
            const driveUrl = groupDetails?.getWorkloadUrl(GroupWorkLoad[GroupWorkLoad.Documents]);
            const taskDetails = taskStore.getTaskDetails(task.id);
            if (taskDetails && driveUrl) {
                const selectedAttachment = Attachment.builder.attachmentFromDriveItem(driveUrl, driveItem).build();
                return taskDetails.attachmentExists(selectedAttachment);
            }
            return false;
        }, /**
     * Fetch the approval details data.
     */ this.fetchApprovalDetailsForTask = async ()=>{
            const { task } = this.state;
            const { actionCreatorProviders } = this.props;
            if (task == null) {
                return;
            }
            const taskActionCreator = await actionCreatorProviders.basicTask();
            taskActionCreator.fetchApprovalDetailsForTask(task.id);
        }, /**
     * Cancel the pending approval of the current task
     */ this.onCancelApproval = async ()=>{
            const { task } = this.state;
            if (task == null || !task.id) {
                return;
            }
            const { actionCreatorProviders } = this.props;
            const taskActionCreator = await actionCreatorProviders.basicTask();
            taskActionCreator.cancelApproval(task.id);
        }, /**
     * Clear the approved approval of the current task
     */ this.onClearApproval = async ()=>{
            const { task, taskStore } = this.state;
            if (task == null || taskStore == null || !task.id) {
                return;
            }
            const { actionCreatorProviders } = this.props;
            const currentTaskDetails = taskStore.getTaskDetails(task.id);
            if (currentTaskDetails == null) {
                return;
            }
            // Update the task's approval information into null
            const updatedTaskDetails = currentTaskDetails.setProperty("approval", null);
            const taskActionCreator = await actionCreatorProviders.basicTask();
            taskActionCreator.updateTaskDetails(currentTaskDetails, updatedTaskDetails);
        }, /**
     * Approve or reject the approval of the current task by approver
     */ this.onUpdateApproval = async (response)=>{
            const { task } = this.state;
            if (task == null || !task.id) {
                return;
            }
            const { actionCreatorProviders } = this.props;
            const taskActionCreator = await actionCreatorProviders.basicTask();
            taskActionCreator.updateApprovalStatus(task.id, response, "");
        }, /**
     * Create the approval for the current task with the approver ID selected by the user
     */ this.onSendApprovalRequest = async ()=>{
            const { task, selectedApprover } = this.state;
            if (task == null || !task.id || selectedApprover == null) {
                return;
            }
            // We are only supporting 1 approver right now
            const { actionCreatorProviders } = this.props;
            const taskActionCreator = await actionCreatorProviders.basicTask();
            const approverIds = [
                selectedApprover.id
            ];
            this.setState({
                selectedApprover: null
            });
            taskActionCreator.createApproval(task.id, approverIds);
        }, /**
     * Called when user select the approver, now we are only supporting 1 approver
     * @param approver The selected approver
     */ this.onSelectApprover = (approver)=>{
            this.setState({
                selectedApprover: approver
            });
        }, this.onSendRequest = ()=>{
            const { task } = this.state;
            if (task == null || !task.id) {
                return null;
            }
            this.setState({
                isCheckApprovalDialogOpen: false
            });
            this.closeEditor();
            this.onSendApprovalRequest();
        }, this.onDiscard = ()=>{
            const { task } = this.state;
            if (task == null || !task.id) {
                return null;
            }
            this.setState({
                isCheckApprovalDialogOpen: false,
                selectedApprover: null
            });
            this.closeEditor();
        }, this.onCloseDialog = ()=>{
            this.setState({
                isCheckApprovalDialogOpen: false
            });
        }, /**
     * Method to render the approval request checking dialog when there is unfinished approval and user is closing the editor
     */ this.renderCheckUnsavedApprovalDialog = ()=>{
            const { task } = this.state;
            if (task == null || !task.id || !this.state.isCheckApprovalDialogOpen) {
                return null;
            }
            const callbacksCheckUnsavedApprovalDialog = {
                onSendRequest: this.onSendRequest,
                onDiscard: this.onDiscard,
                onCloseDialog: this.onCloseDialog
            };
            return /*#__PURE__*/ React.createElement(CheckUnsavedApprovalDialog, {
                callbacks: callbacksCheckUnsavedApprovalDialog
            });
        }, /** Callback to load older chat messages */ this.loadMoreChatMessages = async ()=>{
            const { taskId, actionCreatorProviders } = this.props;
            const chatActionCreator = await actionCreatorProviders.chat();
            chatActionCreator.fetchOlderChatMessages(PlannerChatThreadType.Task, taskId);
        }, /** Callback to send a chat message */ this.onSendMessage = async (parameters)=>{
            const { actionCreatorProviders } = this.props;
            const chatActionCreator = await actionCreatorProviders.chat();
            chatActionCreator.addChatMessage(PlannerChatThreadType.Task, this.props.taskId, parameters);
        }, this.onDeleteMessage = async (messageId)=>{
            const { actionCreatorProviders } = this.props;
            const chatActionCreator = await actionCreatorProviders.chat();
            chatActionCreator.deleteChatMessage(PlannerChatThreadType.Task, this.props.taskId, messageId);
        }, this.onTaskStoreUpdate = ()=>{
            const { taskStore, task } = this.state;
            const { DisableTaskEditorSetTaskStateOptimization } = this.props.configProvider().changeGates;
            const updatedTask = taskStore?.getTask(this.props.taskId).data ?? null;
            if (DisableTaskEditorSetTaskStateOptimization) {
                this.setState({
                    task: updatedTask
                });
            } else if (task !== updatedTask) {
                // only update the state if the task was changed
                this.setState({
                    task: updatedTask
                });
            }
        };
        this.state = {
            appContextStore: null,
            bucketStore: null,
            userMembershipStore: null,
            driveItemStore: null,
            intelligenceStore: null,
            permissionsStore: null,
            planStore: null,
            taskStore: null,
            containerStore: null,
            userStore: null,
            conversationStore: null,
            chatStore: null,
            modalStore: null,
            tenantStore: null,
            goalStore: null,
            task: null,
            currentTaskEditorFocus: TaskEditorDialogFocusOptions.Default,
            attachmentToEdit: null,
            isChildOnedrivePickerOpen: false,
            isDeleteRecurringTaskDialogOpen: false,
            isChildCustomRecurrenceDialogOpen: false,
            isAddFormRequirementDialogOpen: false,
            isRemoveFormConfirmationDialogOpen: false,
            isFormResponseViewDialogOpen: false,
            isEditFormDialogOpen: false,
            createdFormId: "",
            formToRemove: null,
            formToEdit: null,
            isIframeFormRequirementDialogOpen: false,
            formToOpen: null,
            selectedApprover: null,
            isCheckApprovalDialogOpen: false,
            agents: [],
            focusChatOnInit: false
        };
    }
}
